import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import { DatePicker, TextField } from 'office-ui-fabric-react';
import Select, { SingleValue } from 'react-select';
import { Label } from 'reactstrap';
import { AxiosError } from 'axios';

import { securityService } from '@app/Services/SecurityService';
import DateTimeService from '@app/Services/DateTimeService';
import ApiService from '@app/Services/ApiService';
import { DateTimePickerService } from '@app/Services/DateTimePickerService';
import { ApiUrls, Permissions } from '@app/AppConstants';
import { CountryModel } from '@app/Models';
import { SelectOptions } from '@app/Models/Options';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { ModalWindow, ModalDialogOptions, ModalButtonType, modalService, IModalDialogContent } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

type NewDestinationRestrictionDialogProps = {
    personId: string;
    airportOptions: SelectOptions[];
};

@observer
export class NewDestinationItem extends React.Component<NewDestinationRestrictionDialogProps, {}> implements IModalDialogContent<void> {
    private _store: NewDestinationItemStore;

    constructor(props: NewDestinationRestrictionDialogProps) {
        super(props);
        this._store = new NewDestinationItemStore();
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;
        return {
            title: 'New Destination Restriction',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                type: ModalButtonType.Save,
                isDisabled: !this._store.isValidForm || !hasPermission(Permissions.EditObjectPersonDestinationRestriction, !AzureAuth.isReadOnly),
                onClick: async () => {
                    if (await this._store.onSubmit(this.props.personId)){
                        window.close(ModalButtonType.Save);
                    }
                }
            }],
        };
    }

    render() {
        return (
            <>
                <Label className="ms-field-label ms-field-label-required">Airport</Label>
                <Select
                    isMulti={false}
                    placeholder=""
                    options={this.props.airportOptions}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    menuPosition={'fixed'}
                    menuPlacement="bottom"
                    onChange={this._store.onChangeAirport}
                    autoFocus
                    defaultMenuIsOpen={true}
                    classNamePrefix="select-filter"
                    className="select-filter-container"
                    getOptionLabel={option => option.value + ' - ' + option.label}
                />
                <DatePicker
                    label="Date From"
                    className={this.dateClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectFromDate}
                    isRequired={true}
                    value={this._store.dateFrom || undefined} />
                {this._store.isInvalidRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <DatePicker
                    label="Date To"
                    className={this.dateClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectToDate}
                    value={this._store.dateTo || undefined} />
                {this._store.isInvalidRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <TextField
                    label="Comment"
                    multiline
                    autoAdjustHeight
                    validateOnFocusOut
                    onChange={this._store.onCommentChange}
                    value={this._store.comment}
                />
                {this._store.validationMessage && <p className="ms-TextField-errorMessage errorMessage">{this._store.validationMessage}</p>}
            </>
        );
    }

    @computed
    get dateClassName() {
        return 'date-field' + (this._store.isInvalidRange ? ' invalid' : '');
    }
}

class NewDestinationItemStore {
    @observable airportCode: string = '';
    @observable dateFrom: Date | null;
    @observable dateTo: Date | null;
    @observable comment: string;
    @observable validationMessage: string | null = '';

    @action
    onSelectToDate = (date?: Date | null): void => {
        this.dateTo = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
    };

    @action
    onSelectFromDate = (date?: Date | null): void => {
        this.dateFrom = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
    };

    @computed
    get isInvalidRange() {
        if (!this.dateFrom || !this.dateTo) return false;
        return DateTimeService.isBeforeDate(this.dateTo, this.dateFrom);
    }

    @action
    onChangeAirport = (selectedOption: SingleValue<CountryModel>) => {
        if (selectedOption && !Array.isArray(selectedOption)) {
            const selected = selectedOption as CountryModel;
            if (this.airportCode !== selected.value) {
                this.airportCode = selected.value;
            }
        }
    };

    @action
    onCommentChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.comment = newValue || '';
    };

    @action.bound
    async onSubmit(personId: string) {
        try{
            const params = {
                personId: personId,
                airportCode: this.airportCode,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                comment: this.comment
            };
            const { data } = await ApiService.postData(ApiUrls.PersonDestinationRestrictionUrl, params);
            if (!data.hasErrors) {
                this.validationMessage = null;
                NotificationHandler.showSuccess('New destination restriction was added', 'Success message');
                return true;
            } else {
                this.validationMessage = data.errors.join(' ');
            }
        } catch (ex){
            modalService.showError(ex as AxiosError);
        }
        return false;
    }

    @computed
    get isValidForm() {
        return this.airportCode && this.dateFrom && !this.isInvalidRange;
    }
}
